import React, { useEffect, useRef } from "react";
import {
  FaCalendarAlt,
  FaClock,
  FaInfoCircle,
  FaLayerGroup,
  FaMapMarkerAlt,
  FaShareAlt,
  FaTag,
  FaTimes,
  FaTrash,
} from "react-icons/fa";

import {
  formatDateFr,
  formatLongDateFr,
  getEventCategory,
  getEventDescription,
  getEventGradient,
  getEventLevel,
  getEventLocation,
  getEventStatusLabel,
  getEventStatusStyle,
  getEventTitle,
  getEventType,
  getEventTypeStyle,
  isEventShared,
} from "../utils/adminEventsUtils";

function InfoBox({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/[0.04]">
      <div className="flex items-start gap-3">
        {Icon && (
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white text-sky-700 shadow-sm dark:bg-slate-950 dark:text-sky-200">
            <Icon />
          </div>
        )}

        <div className="min-w-0">
          <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
            {label}
          </p>

          <p className="mt-2 break-words text-sm font-bold text-slate-800 dark:text-white/80">
            {value || "Non renseigné"}
          </p>
        </div>
      </div>
    </div>
  );
}

function EventDetailsDrawer({ isOpen, event, onClose, onDelete }) {
  const closeButtonRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;

    closeButtonRef.current?.focus();

    const handleKeyDown = (keyboardEvent) => {
      if (keyboardEvent.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [isOpen, onClose]);

  if (!isOpen || !event) return null;

  const shared = isEventShared(event);
  const description = getEventDescription(event);
  const gradient = getEventGradient(event);

  return (
    <div className="fixed inset-0 z-[90] flex justify-end bg-slate-950/70 backdrop-blur-sm">
      <aside
        className="h-full w-full max-w-3xl bg-white shadow-2xl dark:bg-slate-900"
        role="dialog"
        aria-modal="true"
      >
        <div className="flex h-full flex-col">
          <div className={`bg-gradient-to-br ${gradient} p-5 text-white`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-[1.5rem] bg-white/15 text-2xl backdrop-blur-xl">
                  <FaCalendarAlt />
                </div>

                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.16em] text-white/60">
                    Fiche événement
                  </p>

                  <h2 className="mt-2 font-titre text-2xl font-bold">
                    {getEventTitle(event)}
                  </h2>

                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="inline-flex min-h-8 items-center gap-2 rounded-2xl bg-white/15 px-3 text-xs font-bold text-white">
                      <FaClock />
                      {formatDateFr(event.start)}
                    </span>

                    <span
                      className={`inline-flex min-h-8 items-center rounded-2xl px-3 text-xs font-bold ${getEventStatusStyle(
                        event,
                      )}`}
                    >
                      {getEventStatusLabel(event)}
                    </span>

                    {shared && (
                      <span className="inline-flex min-h-8 items-center gap-2 rounded-2xl bg-white/15 px-3 text-xs font-bold text-white">
                        <FaShareAlt />
                        Partagé
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <button
                ref={closeButtonRef}
                type="button"
                onClick={onClose}
                className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/15 text-white backdrop-blur-xl transition hover:bg-white/25"
                aria-label="Fermer"
              >
                <FaTimes />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4 sm:p-6">
            <div className="grid gap-4 sm:grid-cols-2">
              <InfoBox
                icon={FaTag}
                label="ID événement"
                value={`#${event.id}`}
              />

              <InfoBox
                icon={FaCalendarAlt}
                label="Date"
                value={formatLongDateFr(event.start)}
              />

              <InfoBox
                icon={FaCalendarAlt}
                label="Date de fin"
                value={
                  event.end ? formatLongDateFr(event.end) : "Non renseignée"
                }
              />

              <InfoBox
                icon={FaLayerGroup}
                label="Type"
                value={getEventType(event)}
              />
              <InfoBox
                icon={FaTag}
                label="Niveau"
                value={getEventLevel(event)}
              />
              <InfoBox
                icon={FaTag}
                label="Catégorie"
                value={getEventCategory(event)}
              />
              <InfoBox
                icon={FaMapMarkerAlt}
                label="Lieu"
                value={getEventLocation(event)}
              />
            </div>

            <section className="mt-6 rounded-[2rem] border border-slate-200 bg-white p-5 shadow-lg shadow-slate-200/50 dark:border-white/10 dark:bg-white/[0.04] dark:shadow-black/20">
              <div className="flex items-center gap-3">
                <div
                  className={`flex h-11 w-11 items-center justify-center rounded-2xl ${getEventTypeStyle(event)}`}
                >
                  <FaInfoCircle />
                </div>

                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
                    Informations complémentaires
                  </p>

                  <h3 className="font-titre text-xl font-bold text-slate-950 dark:text-white">
                    Description
                  </h3>
                </div>
              </div>

              {description ? (
                <p className="mt-4 whitespace-pre-line text-sm font-medium leading-7 text-slate-600 dark:text-white/60">
                  {description}
                </p>
              ) : (
                <div className="mt-4 rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm font-bold text-slate-400 dark:border-white/10 dark:bg-slate-950">
                  Aucune description renseignée pour cet événement.
                </div>
              )}
            </section>

            <section className="mt-6 rounded-[2rem] border border-slate-200 bg-white p-5 shadow-lg shadow-slate-200/50 dark:border-white/10 dark:bg-white/[0.04] dark:shadow-black/20">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-indigo-100 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-200">
                  <FaShareAlt />
                </div>

                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
                    Visibilité
                  </p>

                  <h3 className="font-titre text-xl font-bold text-slate-950 dark:text-white">
                    {shared ? "Événement partagé" : "Événement non partagé"}
                  </h3>
                </div>
              </div>

              <p className="mt-4 text-sm font-medium leading-7 text-slate-500 dark:text-white/50">
                {shared
                  ? "Cet événement peut être visible dans les espaces partagés ou publics selon ta configuration actuelle."
                  : "Cet événement reste privé ou limité à l’administration selon ta configuration actuelle."}
              </p>
            </section>
          </div>

          <div className="sticky bottom-0 border-t border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-slate-900">
            <div className="grid gap-2 sm:grid-cols-[1fr_auto]">
              <button
                type="button"
                onClick={onClose}
                className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-2xl bg-blue-700 px-5 py-3 text-sm font-bold text-white transition hover:-translate-y-0.5 hover:bg-blue-800"
              >
                <FaTimes />
                Fermer la fiche événement
              </button>

              <button
                type="button"
                onClick={() => onDelete(event.id)}
                className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl border border-red-200 bg-red-50 px-5 py-3 text-sm font-bold text-red-700 transition hover:-translate-y-0.5 hover:bg-red-100 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-200"
              >
                <FaTrash />
                Supprimer
              </button>
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
}

export default EventDetailsDrawer;
